import { memo, useDeferredValue, useRef, useMemo, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Pencil, Copy, UserX, UserCheck, ExternalLink, Link, Check, Clock } from "lucide-react";
import {
  Checkbox, Skeleton, Tooltip, TooltipContent, TooltipTrigger,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import type { UserItem, UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";
import { isSuperTenantMember } from "@/features/identity-hub/utils/iam.utils";
import { relativeTime } from "@/features/identity-hub/utils/time";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { UserPhotoMark } from "./UserPhotoMark";
import { OrgLogoMark } from "./OrgLogoMark";
import { SuperTenantBadge } from "./SuperTenantBadge";
import { AuthProviderBadge } from "./shared/AuthProviderBadge";
import { UserStatusBadge } from "./shared/UserStatusBadge";
import { TableEmptyState } from "./shared/TableEmptyState";
import { RowActionsTrigger } from "./shared/RowActionsTrigger";
import { SortHeader } from "./shared/table/SortHeader";
import { useTableVirtualizer } from "./shared/table/useTableVirtualizer";
import { useRowFlash } from "./shared/table/useRowFlash";
import { ROW_FLASH_STYLE_TAG } from "./shared/table/rowFlashStyle";

// ─── Shared types ────────────────────────────────────────────────────

export type SortKey = "name" | "email" | "status" | "orgs" | "role" | "createdAt";

export interface SortState {
  key: SortKey;
  dir: "asc" | "desc";
}

export interface PeopleFilters {
  search: string;
  orgId: string | null;
  role: string | null;
  status: string | null;
  noRole: boolean;
  neverSignedIn: boolean;
  hasPendingInvite: boolean;
}

// ─── Sub-components ───────────────────────────────────────────────────

function OrgChips({
  tenants,
  highlightId,
}: {
  tenants: Array<{ id: string; name: string; logoUrl?: string | null }>;
  highlightId?: string | null;
}) {
  const { t } = useTranslation();
  const max = 4;
  const visible = tenants.slice(0, max);
  const extra = tenants.length - max;
  // When a filter match exists, let contrast carry the signal: the matched org
  // stays full-strength with a crisp chip-hugging ring, siblings recede.
  const hasMatch = !!highlightId && tenants.some((tenant) => tenant.id === highlightId);
  return (
    <div className="flex flex-wrap items-center gap-0.5">
      {visible.map((tenant) => {
        const isMatch = hasMatch && tenant.id === highlightId;
        return (
          <Tooltip key={tenant.id}>
            <TooltipTrigger asChild>
              {/* Wrapper span always forwards the Radix trigger ref/props — the
                  logo mark renders as bare initials (no button) when the org has
                  no logo, which would otherwise drop the tooltip. The ring radius
                  mirrors OrgLogoMark's `rounded` (size ≤ 24) so it hugs the mark
                  instead of floating a circular halo around it. */}
              <span
                className={cn(
                  "inline-flex rounded transition-opacity duration-150 ease-out",
                  isMatch && "ring-2 ring-primary",
                  hasMatch && !isMatch && "opacity-40",
                )}
              >
                <ImagePreviewTrigger src={tenant.logoUrl} alt={tenant.name}>
                  <OrgLogoMark name={tenant.name} logoUrl={tenant.logoUrl} size={20} />
                </ImagePreviewTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isMatch ? t("iam.dashboard.people.orgChips.matchesFilter", { name: tenant.name }) : tenant.name}
            </TooltipContent>
          </Tooltip>
        );
      })}
      {extra > 0 && (
        <span className={cn(
          "inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-medium text-muted-foreground transition-opacity duration-150 ease-out",
          hasMatch && "opacity-40",
        )}>
          +{extra}
        </span>
      )}
    </div>
  );
}

// `InviteExpiryCell` no longer carries the inline copy-link affordance —
// the API stopped surfacing the bearer `token` on listing reads, so a copy
// action would have to round-trip through /invites/regenerate. We keep that
// behavior in the row's overflow menu (which already accepts an
// `onCopyInviteLink` callback) and leave the cell as a pure expiry badge.
function InviteExpiryCell({ invite }: { invite: { expiresAt: string } }) {
  const { t } = useTranslation();
  const days = Math.ceil((new Date(invite.expiresAt).getTime() - Date.now()) / 86_400_000);
  const label = days <= 0
    ? t("iam.dashboard.people.inviteExpiry.lessThanOneDay", "< 1d left")
    : t("iam.dashboard.people.inviteExpiry.daysLeft", { days });
  const exactDate = new Date(invite.expiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn(
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums",
          days < 3  ? "bg-destructive/10 text-destructive border-destructive/20"
        : days < 7  ? "bg-warning-subtle text-warning-subtle-foreground border-warning-border"
        :             "bg-muted text-muted-foreground border-border",
        )}>
          <Clock className="h-2.5 w-2.5 shrink-0" />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{t("iam.dashboard.people.inviteExpiry.tooltip", { date: exactDate })}</TooltipContent>
    </Tooltip>
  );
}

// ─── Derived row type ─────────────────────────────────────────────────

interface PersonRow extends UserItem {
  tenants: Array<{ id: string; name: string; logoUrl?: string | null }>;
  primaryRole: string | null;
  totalRoles: number;
  accessLoaded: boolean;
  isSuperTenant: boolean;
  /** Tenant id matched by the active org filter — highlighted in the Orgs column. */
  matchedTenantId: string | null;
  /** True when the displayed primary role is the one matched by the active role filter. */
  roleMatched: boolean;
}

interface DeriveContext {
  /** Active org filter (tenant id) — the matched chip is pulled to the front and highlighted. */
  filterOrgId?: string | null;
  /** Active role filter (role name) — the matched role becomes the highlighted primary pill. */
  filterRole?: string | null;
}

function deriveRows(
  users: UserItem[],
  accessByUser: Map<string, UserAccessEntry[]>,
  ctx: DeriveContext = {},
): PersonRow[] {
  const { filterOrgId = null, filterRole = null } = ctx;
  return users.map((u) => {
    const entries = accessByUser.get(u.id);
    if (!entries) {
      return { ...u, tenants: [], primaryRole: null, totalRoles: 0, accessLoaded: false, isSuperTenant: false, matchedTenantId: null, roleMatched: false };
    }
    const tenantMap = new Map<string, { id: string; name: string; logoUrl?: string | null }>();
    for (const e of entries) {
      if (!tenantMap.has(e.access.tenantId)) {
        tenantMap.set(e.access.tenantId, {
          id: e.access.tenantId,
          name: e.access.tenant.name,
          logoUrl: e.access.tenant.logoUrl,
        });
      }
    }
    let tenants = Array.from(tenantMap.values());
    // Surface the org the caller filtered by: pull it to the front so it's
    // always in the visible (non-"+N") window and can be highlighted.
    const matchedTenantId = filterOrgId && tenantMap.has(filterOrgId) ? filterOrgId : null;
    if (matchedTenantId) {
      tenants = [
        ...tenants.filter((t) => t.id === matchedTenantId),
        ...tenants.filter((t) => t.id !== matchedTenantId),
      ];
    }
    // When a role filter is active and this person carries that role, show it
    // as the (highlighted) primary pill instead of the first-listed role.
    const roleMatched = !!filterRole && entries.some((e) => e.access.name === filterRole);
    const primaryRole = roleMatched ? filterRole : entries[0]?.access.name ?? null;
    return {
      ...u,
      tenants,
      primaryRole,
      totalRoles: entries.length,
      accessLoaded: true,
      isSuperTenant: isSuperTenantMember(entries),
      matchedTenantId,
      roleMatched,
    };
  });
}

// ─── PersonRow (memoized) ─────────────────────────────────────────────
//
// Without memoization the table re-rendered all rows on every dashboard
// state change (filter focus, KPI hover, etc.) — turning each click into
// a 100-row reconciliation across 100 Radix DropdownMenu roots and
// hundreds of Tooltips. With primitive props + stable id-based callbacks
// React.memo skips rows whose data, selection, active and flash state
// are unchanged.

interface PersonRowProps {
  row: PersonRow;
  isSelected: boolean;
  isActive: boolean;
  onToggleRow: (id: string) => void;
  onRowClick?: (id: string) => void;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
  /** Trigger /invites/regenerate for the row's email and copy the resulting share link. */
  onCopyInviteLink?: (user: UserItem, accessId: string) => void;
}

function PersonRowImpl({
  row, isSelected, isActive,
  onToggleRow, onRowClick, onEditProfile, onSuspend, onUnsuspend, onOpenAuditLog, onCopyInviteLink,
}: PersonRowProps) {
  const { t } = useTranslation();
  return (
    <tr
      data-rowid={row.id}
      onClick={() => onRowClick?.(row.id)}
      className={cn(
        "group cursor-pointer transition-colors",
        isActive ? "bg-primary/5 hover:bg-primary/5" : isSelected ? "bg-primary/5" : "hover:bg-muted/40",
      )}
    >
      <td className="w-10 px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleRow(row.id)}
          aria-label={t("iam.dashboard.people.columns.select", { name: row.displayName })}
        />
      </td>

      {/* Name */}
      <td className="w-36 max-w-[144px] overflow-hidden px-3 py-3">
        <div className="flex items-center gap-2">
          <ImagePreviewTrigger src={row.photoUrl} alt={row.displayName}>
            <UserPhotoMark name={row.displayName} photoUrl={row.photoUrl} size="sm" className="shrink-0" />
          </ImagePreviewTrigger>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="truncate font-medium text-foreground">{row.displayName}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{row.displayName}</TooltipContent>
          </Tooltip>
          {row.isSuperTenant && <SuperTenantBadge />}
        </div>
      </td>

      {/* Email */}
      <td className="hidden w-44 max-w-[176px] overflow-hidden px-3 py-3 sm:table-cell">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block truncate text-xs text-muted-foreground">{row.email}</span>
          </TooltipTrigger>
          <TooltipContent side="top">{row.email}</TooltipContent>
        </Tooltip>
      </td>

      {/* Sign-in method */}
      <td className="hidden w-24 px-3 py-3 md:table-cell">
        <AuthProviderBadge provider={row.authProvider} variant="cell" />
      </td>

      {/* Status */}
      <td className="px-3 py-3">
        <UserStatusBadge status={row.status} />
      </td>

      {/* Orgs */}
      <td className="hidden px-3 py-3 sm:table-cell">
        {row.accessLoaded ? (
          row.tenants.length > 0
            ? <OrgChips tenants={row.tenants} highlightId={row.matchedTenantId} />
            : <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <Skeleton className="h-[22px] w-16 rounded-full" />
        )}
      </td>

      {/* Primary role */}
      <td className="hidden px-3 py-3 md:table-cell">
        {row.accessLoaded ? (
          row.primaryRole ? (
            <div className="flex items-center gap-1.5">
              {row.roleMatched ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {row.primaryRole}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">{t("iam.dashboard.people.actions.matchesRoleFilter", "Matches your role filter")}</TooltipContent>
                </Tooltip>
              ) : (
                <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                  {row.primaryRole}
                </span>
              )}
              {row.totalRoles > 1 && (
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  +{row.totalRoles - 1}
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )
        ) : (
          <Skeleton className="h-5 w-24 rounded-md" />
        )}
      </td>

      {/* Last active / invite expiry */}
      <td className="hidden px-3 py-3 lg:table-cell">
        {row.pendingInvite
          ? <InviteExpiryCell invite={row.pendingInvite} />
          : <span className="text-xs tabular-nums text-muted-foreground">{row.lastSeenAt ? relativeTime(row.lastSeenAt) : "—"}</span>}
      </td>

      {/* Actions */}
      <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <RowActionsTrigger variant="hover-group" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {row.pendingInvite && onCopyInviteLink && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={() => onCopyInviteLink(row, row.pendingInvite!.accessId)}
                    >
                      <Link className="h-3.5 w-3.5" />
                      {t("iam.dashboard.people.actions.copyInviteLink", "Copy invite link")}
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[220px]">
                    {t("iam.dashboard.people.actions.copyInviteLinkTip", "Copy this person's pending invite link to share it with them directly.")}
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuSeparator />
              </>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem className="gap-2" onClick={() => onEditProfile?.(row)}>
                  <Pencil className="h-3.5 w-3.5" />
                  {t("iam.dashboard.people.actions.editProfile", "Edit profile")}
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                {t("iam.dashboard.people.actions.editProfileTip", "Update this person's name, photo, and other profile details.")}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => navigator.clipboard.writeText(row.email)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t("iam.dashboard.people.actions.copyEmail", "Copy email")}
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                {t("iam.dashboard.people.actions.copyEmailTip", "Copy this person's email address to the clipboard.")}
              </TooltipContent>
            </Tooltip>
            {onOpenAuditLog && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={() => onOpenAuditLog({ searchPrefill: row.email })}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t("iam.dashboard.people.actions.viewAuditLog", "View audit log")}
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[220px]">
                  {t("iam.dashboard.people.actions.viewAuditLogTip", "See the full history of actions and changes for this person.")}
                </TooltipContent>
              </Tooltip>
            )}
            {row.status === "DEACTIVATED" ? (
              <>
                <DropdownMenuSeparator />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem className="gap-2" onClick={() => onUnsuspend?.(row)}>
                      <UserCheck className="h-3.5 w-3.5" />
                      {t("iam.dashboard.people.actions.unsuspendAccount", "Unsuspend account")}
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[220px]">
                    {t("iam.dashboard.people.actions.unsuspendAccountTip", "Restore access so this person can sign in again.")}
                  </TooltipContent>
                </Tooltip>
              </>
            ) : row.status !== "INACTIVE" && (
              <>
                <DropdownMenuSeparator />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => onSuspend?.(row)}
                    >
                      <UserX className="h-3.5 w-3.5" />
                      {t("iam.dashboard.people.actions.suspendAccount", "Suspend account")}
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[220px]">
                    {t("iam.dashboard.people.actions.suspendAccountTip", "Block this person from signing in. Their access is kept and can be restored later.")}
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

const PersonRow = memo(PersonRowImpl);

// ─── PeopleTable ──────────────────────────────────────────────────────

interface PeopleTableProps {
  users: UserItem[];
  accessByUser: Map<string, UserAccessEntry[]>;
  sort: SortState;
  onSort: (key: SortKey) => void;
  selected: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: (ids: string[]) => void;
  activeId?: string | null;
  onRowClick?: (id: string) => void;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
  /** Trigger /invites/regenerate for the row's pending invite and copy the share link. */
  onCopyInviteLink?: (user: UserItem, accessId: string) => void;
  flashId?: string | null;
  /**
   * Vertical scroll container for row virtualization. The dashboard owns
   * the scroller (the `overflow-y-auto` wrapper around the tab content)
   * and passes its ref down — only the visible window of rows mounts,
   * regardless of how many users the tenant has.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>;
  hasMore?: boolean;
  isFetchingNextPage?: boolean;
  /** Active org filter (tenant id) — matched org chip is pulled forward + highlighted. */
  filterOrgId?: string | null;
  /** Active role filter (role name) — matched role becomes the highlighted primary pill. */
  filterRole?: string | null;
}

const TABLE_COLUMN_COUNT = 9;

export function PeopleTable({
  users, accessByUser, sort, onSort,
  selected, onToggleRow, onToggleAll,
  activeId, onRowClick, onEditProfile, onSuspend, onUnsuspend,
  onOpenAuditLog, onCopyInviteLink,
  flashId,
  scrollContainerRef,
  hasMore = false,
  isFetchingNextPage = false,
  filterOrgId = null,
  filterRole = null,
}: PeopleTableProps) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);

  const deferredUsers = useDeferredValue(users);
  const deferredAccess = useDeferredValue(accessByUser);
  const deferredOrgId = useDeferredValue(filterOrgId);
  const deferredRole = useDeferredValue(filterRole);
  const rows = useMemo(
    () => deriveRows(deferredUsers, deferredAccess, { filterOrgId: deferredOrgId, filterRole: deferredRole }),
    [deferredUsers, deferredAccess, deferredOrgId, deferredRole],
  );

  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allSelected = rowIds.length > 0 && rowIds.every((id) => selected.has(id));
  const someSelected = !allSelected && rowIds.some((id) => selected.has(id));

  const { virtualizer, virtualItems, topPad, bottomPad } = useTableVirtualizer({
    count: rows.length,
    scrollContainerRef,
  });

  useRowFlash({ flashId, rows, virtualizer, tableRef });

  if (rows.length === 0 && !isFetchingNextPage) {
    return (
      <TableEmptyState
        icon={ChevronDown}
        title={t("iam.dashboard.people.empty.title", "No people match your filters")}
        subtitle={t("iam.dashboard.people.empty.subtitle", "Try clearing the filters or broadening your search.")}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <style>{ROW_FLASH_STYLE_TAG}</style>
      <div className="overflow-x-auto">
        <table ref={tableRef} className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-card">
            <tr className="group border-b border-border bg-muted/30 text-left text-xs font-medium text-muted-foreground">
              <th className="w-10 px-3 py-2.5 text-left">
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={() => onToggleAll(rowIds)}
                  aria-label={t("iam.dashboard.people.columns.selectAll", "Select all")}
                />
              </th>
              <SortHeader id="name"      label={t("iam.dashboard.people.columns.name", "Name")}         sort={sort} onSort={onSort} className="w-36" />
              <SortHeader id="email"     label={t("iam.dashboard.people.columns.email", "Email")}        sort={sort} onSort={onSort} className="hidden w-44 sm:table-cell" />
              <th className="hidden w-24 px-3 py-2.5 text-left text-xs font-medium text-muted-foreground md:table-cell">{t("iam.dashboard.people.columns.signIn", "Sign-in")}</th>
              <SortHeader id="status"    label={t("iam.dashboard.people.columns.status", "Status")}       sort={sort} onSort={onSort} className="w-24" />
              <SortHeader id="orgs"      label={t("iam.dashboard.people.columns.orgs", "Orgs")}         sort={sort} onSort={onSort} className="hidden w-36 sm:table-cell" />
              <SortHeader id="role"      label={t("iam.dashboard.people.columns.role", "Primary role")} sort={sort} onSort={onSort} className="hidden w-36 md:table-cell" />
              <SortHeader id="createdAt" label={t("iam.dashboard.people.columns.lastActive", "Last active")}  sort={sort} onSort={onSort} className="hidden w-28 lg:table-cell" />
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {topPad > 0 && (
              <tr aria-hidden style={{ height: topPad }}>
                <td colSpan={TABLE_COLUMN_COUNT} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
            {virtualItems.map((vi) => {
              const row = rows[vi.index];
              if (!row) return null;
              return (
                <PersonRow
                  key={row.id}
                  row={row}
                  isSelected={selected.has(row.id)}
                  isActive={activeId === row.id}
                  onToggleRow={onToggleRow}
                  onRowClick={onRowClick}
                  onEditProfile={onEditProfile}
                  onSuspend={onSuspend}
                  onUnsuspend={onUnsuspend}
                  onOpenAuditLog={onOpenAuditLog}
                  onCopyInviteLink={onCopyInviteLink}
                />
              );
            })}
            {bottomPad > 0 && (
              <tr aria-hidden style={{ height: bottomPad }}>
                <td colSpan={TABLE_COLUMN_COUNT} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pager footer */}
      <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
        <span>
          {t("iam.dashboard.people.footer.loadedCount", { count: rows.length })}
          {hasMore ? t("iam.dashboard.people.footer.scrollForMore", " (scroll for more)") : ""}
        </span>
        {isFetchingNextPage && (
          <span className="text-muted-foreground">{t("iam.dashboard.people.footer.loadingMore", "Loading more…")}</span>
        )}
      </div>
    </div>
  );
}
